import { Component, OnInit, ViewChild, TemplateRef, AfterViewInit, inject, OnDestroy } from '@angular/core';
import { CompanyModel } from '../../..//models/company.model';
import { CompaniesService } from '../../../services/company.service';
import { RolesService } from '../../../services/roles.service';
import { ToastService } from '../../..//services/toast.service';
import { RoleGrantModel } from '../../../models/role-grant.model';
import { BehaviorSubject, Subscription } from 'rxjs';
import { CompanyRoleModel } from '../../../models/company-role.model';
import { GlobalStoreService } from '../../../services/global-store.service';
import { DrawerComponent } from '../../layout/kt/components/_DrawerComponent';
import { Permission } from '../../../models/permission.model';
import { ResourceType } from '../../../enums/resource-type.enum';
import { SharedImports } from '../../../shared-imports';

@Component({
	selector: 'app-create-company-roles-drawer',
	templateUrl: './create-company-roles-drawer.component.html',
	imports: [SharedImports],
})
export class CreateCompanyRolesDrawerComponent implements OnInit, AfterViewInit, OnDestroy {
	@ViewChild('resourceTemplate', { static: true }) resourceTemplate: TemplateRef<any>;
	@ViewChild('permissionsTemplate', { static: true }) permissionsTemplate: TemplateRef<any>;

	activeRole: CompanyRoleModel | null;
	roleName: string = '';
	roleActive: boolean = false;
	activeCompany: CompanyModel;
	permissions$: BehaviorSubject<Permission[] | any> = new BehaviorSubject<Permission[] | any>(null);
	tableFields: any = [];

	private _subscriptions: Subscription[] = [];
	private mutationObserver: MutationObserver | null = null;

	roleNameError = false;
	roleIsCreatedOrUpdated = true;

	private rolesService = inject(RolesService);
	private companiesService = inject(CompaniesService);
	private toastService = inject(ToastService);
	private globalStoreService = inject(GlobalStoreService);

	constructor() {
		this._subscriptions.push(
			this.companiesService.company$.subscribe({
				next: (res) => {
					if (res && res?.uuid) {
						this.activeCompany = res;
					}
				},
				error: (err) => {},
				complete: () => {},
			})
		);
		this._subscriptions.push(
			this.globalStoreService.editingRole$.subscribe((role) => {
				if (role) {
					this.activeRole = role;
					this.roleName = role.name;
					this.roleActive = role.active;
					this.processGrantList(role.grantlist);
				} else {
					this.activeRole = null;
					this.processGrantList(null);
				}
			})
		);
	}

	ngOnInit(): void {
		this.tableFields = [
			{
				label: 'create_role_drawer_label_resource',
				field: 'resource',
				type: 'templated',
				orderItems: false,
				class: 'auto',
				template: this.resourceTemplate,
			},
			{
				label: 'create_role_drawer_label_permissions',
				field: 'permissions',
				type: 'templated',
				orderItems: false,
				class: 'auto',
				template: this.permissionsTemplate,
			},
		];
		this.permissions$.next([
			{
				label: 'create_role_drawer_label_company',
				resource: ResourceType.COMPANIES,
				create: false,
				read: false,
				update: false,
				delete: false,
			},
			{
				label: 'create_role_drawer_label_role',
				resource: ResourceType.ROLES,
				create: false,
				read: false,
				update: false,
				delete: false,
			},
			{
				label: 'create_role_drawer_label_users',
				resource: ResourceType.USERS,
				create: false,
				read: false,
				update: false,
				delete: false,
			},
			{
				label: 'create_role_drawer_label_invites',
				resource: ResourceType.INVITES,
				create: false,
				read: false,
				update: false,
				delete: false,
			},
			{
				label: 'create_role_drawer_label_api_keys',
				resource: ResourceType.API_KEYS,
				create: false,
				read: false,
				update: false,
				delete: false,
			},
		]);
	}

	ngAfterViewInit(): void {
		if (this.mutationObserver) {
			this.mutationObserver.disconnect();
			this.mutationObserver = null;
		}

		this.mutationObserver = new MutationObserver(() => {
			const drawer = DrawerComponent.getInstance('kt_create_company_role');
			if (drawer) {
				drawer.on('kt.drawer.show', () => {
					this.roleIsCreatedOrUpdated = false;
				});

				drawer.on('kt.drawer.hide', () => {
					this.showCancelToast();
					this.ngOnDestroy();
				});
				this.mutationObserver?.disconnect();
				this.mutationObserver = null;
			}
		});

		this.mutationObserver.observe(document.body, {
			childList: true,
			subtree: true,
		});
	}

	saveCompanyRole(): void {
		this.validateRoleName();
		if (this.roleNameError) {
			return;
		}

		const grantlist: RoleGrantModel[] = [];

		this.permissions$.value.forEach((permission: any) => {
			if (permission.create) {
				grantlist.push({
					role: this.roleName,
					resource: permission.resource,
					action: 'create:any',
					attributes: [],
				});
			}

			if (permission.read) {
				grantlist.push({
					role: this.roleName,
					resource: permission.resource,
					action: 'read:any',
					attributes: [],
				});
			}

			if (permission.update) {
				grantlist.push({
					role: this.roleName,
					resource: permission.resource,
					action: 'update:any',
					attributes: [],
				});
			}

			if (permission.delete) {
				grantlist.push({
					role: this.roleName,
					resource: permission.resource,
					action: 'delete:any',
					attributes: [],
				});
			}
		});

		let roleData = {
			name: this.roleName,
			active: this.roleActive,
			grantlist: grantlist,
		};

		if (this.globalStoreService.editingRole$.value && this.activeRole) {
			this.rolesService.patchOneCompanyRole(this.activeCompany.uuid, this.activeRole.uuid, roleData).subscribe({
				next: () => {
					this.roleIsCreatedOrUpdated = true;
					this.toastService.success('toast_role_updated', 'Success');
					this.onClose();
				},
				error: (err) => {
					this.roleIsCreatedOrUpdated = false;
					this.toastService.error('toast_error_updating_role', 'Error');
				},
			});
		} else {
			this.rolesService.postOneCompanyRole(this.activeCompany.uuid, roleData).subscribe({
				next: () => {
					this.roleIsCreatedOrUpdated = true;
					this.toastService.success('toast_role_created', 'Success');
					this.onClose();
				},
				error: (err) => {
					this.roleIsCreatedOrUpdated = false;
					if (err.status === 400) {
						this.toastService.warning('toast_error_creating_role', 'Warning');
						return;
					}
					this.toastService.error('toast_error_creating_role', 'Error');
				},
			});
		}
	}

	private processGrantList(grantlist: RoleGrantModel[] | null = null): void {
		if (grantlist) {
			grantlist.forEach((grant: any) => {
				const permission = this.permissions$.value.find((p: any) => p.resource === grant.resource);
				if (permission) {
					if (grant.action === 'create:any') permission.create = true;
					if (grant.action === 'read:any') permission.read = true;
					if (grant.action === 'update:any') permission.update = true;
					if (grant.action === 'delete:any') permission.delete = true;
				}
			});
		} else {
			if (this.permissions$.value) {
				this.permissions$.value.forEach((el: any) => {
					el.create = false;
					el.update = false;
					el.delete = false;
					el.read = false;
				});
				this.permissions$.next(this.permissions$.value);
			}
		}
	}

	showCancelToast(): void {
		if (!this.roleIsCreatedOrUpdated) {
			if (this.activeRole) {
				this.toastService.info('toast_role_not_updated', 'Info');
			} else {
				this.toastService.info('toast_role_not_created', 'Info');
			}
			this.roleIsCreatedOrUpdated = true;
		}
	}

	validateRoleName(): void {
		this.roleNameError = !this.roleName?.trim();
	}

	catchItemsToSort(event: any) {}

	catchItemsToDelete(event: any) {}

	ngOnDestroy(): void {
		this.activeRole = null;
		this.roleName = '';
		this.roleActive = false;
		this.processGrantList(null);
		this.roleNameError = false;
		if (this.mutationObserver) {
			this.mutationObserver.disconnect();
			this.mutationObserver = null;
		}
	}

	onClose() {
		const drawer = DrawerComponent.getInstance('kt_create_company_role');
		if (drawer) {
			drawer.hide();
		}
		this.ngOnDestroy();
	}
}
